import { VitrinBanner, VitrinBannerGroup } from "@/models/home/VitrinBanner";
import { VitrinCategory } from "@/models/home/VitrinCategory";
import { VitrinCourse } from "@/models/home/VitrinCourse";
import { VitrinFeaturedSection } from "@/models/home/VitrinFeaturedSection";
import { VitrinMiniAppHeader } from "@/models/miniapp/VitrinMiniAppHeader";
import { SearchResultData } from "@/models/search/SearchResult";
import { SearchSuggestions } from "@/models/search/SearchSuggestions";
import { PopularSearchTerm } from "@/models/search/PopularSearches";
import {
  VitrinCourseDetail,
  VitrinCourseDetailLessonItem,
} from "@/models/course/VitrinCourseDetail";
import { fetchInstance } from "@/utils/fetchInstance";
import { VitrinListedCourse } from "@/models/course/VitrinListedCourse";
import { getBuyAttribution } from "@/features/analytics";

export interface BuyCourseForm {
  action?: string;
  method?: string;
  inputs?: Record<string, string | number | boolean | null | undefined>;
  fields?: Record<string, string | number | boolean | null | undefined>;
}

export interface BuyCourseResponse {
  enrolled?: boolean;
  form?: BuyCourseForm;
}

/** Payment method for the buy endpoint. Omit for the default card gateway. */
export type PaymentMethod = "snapppay";

/**
 * SnappPay installment eligibility. The `title`/`description` come live from
 * SnappPay and must be displayed verbatim (never hardcoded or cached). It fails
 * closed: `eligible: false` for free/out-of-range amounts or any backend error.
 */
export interface SnappPayEligibility {
  eligible: boolean;
  title?: string;
  description?: string;
  /** Toman amount the eligibility was computed for (reference only). */
  amount?: number;
}

export interface CourseContentAttachment {
  id?: number;
  url?: string;
  mime_type?: string;
  file_name?: string;
  size?: number;
}

export interface CourseContentExternalMedia {
  id: string;
  media_type: string;
  media_url: string;
  media_token?: string;
}

export interface CourseContent {
  id: number;
  type: number;
  type_label?: string;
  text_content?: string;
  sort_order?: number;
  attachments?: CourseContentAttachment[] | null;
  external_media?: CourseContentExternalMedia;
}

export class VitrinService {
  private static buildLimitQuery(limit?: number) {
    const searchParams = new URLSearchParams();
    if (typeof limit === "number") {
      searchParams.set("limit", String(limit));
    }

    const query = searchParams.toString();
    return query ? `?${query}` : "";
  }

  static async getBanners(limit?: number) {
    const endpoint = `vitrin/banners${this.buildLimitQuery(limit)}`;
    return fetchInstance<VitrinBannerGroup[]>(endpoint, { method: "GET" });
  }

  static async getBannersByPosition(position: string, limit?: number) {
    const endpoint = `vitrin/banners/${encodeURIComponent(position)}${this.buildLimitQuery(limit)}`;
    return fetchInstance<VitrinBanner[]>(endpoint, { method: "GET" });
  }

  /**
   * Every featured rail in one request, already sorted by display order.
   * Sections that currently resolve to zero items are hidden by the backend
   * unless `includeEmpty` is set, so an empty carousel never reaches the page.
   */
  static async getFeaturedSections(includeEmpty = false) {
    const endpoint = `vitrin/featured-sections${includeEmpty ? "?include_empty=true" : ""}`;
    return fetchInstance<VitrinFeaturedSection[]>(endpoint, { method: "GET" });
  }

  /**
   * One rail by its admin key, e.g. `home_top_courses`. Used where the page
   * already knows which rail goes in which slot, so each block fetches (and
   * caches, and fails) on its own instead of waiting on the full list.
   */
  static async getFeaturedSection(key: string) {
    const endpoint = `vitrin/featured-sections/${encodeURIComponent(key)}`;
    return fetchInstance<VitrinFeaturedSection>(endpoint, { method: "GET" });
  }

  static async getMiniAppHeader(miniAppId: string | number) {
    const endpoint = `vitrin/mini-apps/${miniAppId}/header`;
    return fetchInstance<VitrinMiniAppHeader>(endpoint, { method: "GET" });
  }

  static async getMiniAppCourses(
    miniAppId: string | number,
    params?: {
      filter?: {
        global?: string;
        title?: string;
        category_id?: number | number[];
        discount_min?: number;
        discount_max?: number;
        is_free?: boolean;
      };
      sort?: string;
      page?: number;
      per_page?: number;
    },
  ) {
    const searchParams = new URLSearchParams();
    const f = params?.filter;

    this.appendFilterParam(searchParams, "global", f?.global);
    this.appendFilterParam(searchParams, "title", f?.title);
    this.appendFilterParam(searchParams, "category_id", f?.category_id);
    this.appendFilterParam(searchParams, "discount_min", f?.discount_min);
    this.appendFilterParam(searchParams, "discount_max", f?.discount_max);
    this.appendFilterParam(searchParams, "is_free", f?.is_free);

    if (params?.sort) {
      searchParams.set("sort", params.sort);
    }

    if (typeof params?.page === "number") {
      searchParams.set("page", String(params.page));
    }

    if (typeof params?.per_page === "number") {
      searchParams.set("per_page", String(params.per_page));
    }

    const queryString = searchParams.toString();
    const endpoint = `vitrin/mini-apps/${miniAppId}/courses${
      queryString ? `?${queryString}` : ""
    }`;

    return fetchInstance<VitrinListedCourse[]>(endpoint, { method: "GET" });
  }

  /**
   * Filters go over the wire as `filter[key]=value`, and a multi-select as a
   * **comma-separated list** — `filter[category_id]=61,140`. Neither of the two
   * shapes we used to send is part of the contract: repeating the bare key
   * (`filter[category_id]=61&filter[category_id]=140`) keeps only the last
   * value, and the PHP array form (`filter[category_id][]=`) is undocumented.
   *
   * An empty value means "don't care" to the API, so an empty string or an
   * empty list drops the key entirely rather than sending it blank.
   */
  private static appendFilterParam(
    searchParams: URLSearchParams,
    key: string,
    value: string | number | boolean | (string | number)[] | undefined,
  ) {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      const csv = value
        .filter(
          (v) =>
            v !== undefined &&
            v !== null &&
            v !== "" &&
            (typeof v !== "number" || !Number.isNaN(v)),
        )
        .join(",");
      if (csv) searchParams.set(`filter[${key}]`, csv);
      return;
    }

    if (typeof value === "number" && Number.isNaN(value)) return;
    if (typeof value === "boolean") {
      searchParams.set(`filter[${key}]`, value ? "1" : "0");
      return;
    }
    if (value === "") return;

    searchParams.set(`filter[${key}]`, String(value));
  }

  static async getCourses(params?: {
    filter?: {
      global?: string | string[];
      title?: string | string[];
      category_id?: number | number[];
      category_title?: string;
      price_min?: number;
      price_max?: number;
      discount_min?: number;
      discount_max?: number;
      created_from?: string;
      created_to?: string;
      users_min?: number;
      users_max?: number;
      /**
       * True asks for genuinely free courses. Not the same as `price_max: 0`,
       * which matches on the *effective* price and so also sweeps in a paid
       * course that a 100% discount happens to have zeroed out today.
       */
      is_free?: boolean;
      has_certificate?: boolean;
      has_quiz?: boolean;
      has_online_class?: boolean;
      /** 1=مقدماتی 2=متوسط 3=پیشرفته 4=همه سطوح. Multi-select as a list. */
      level?: number | number[];
      /** `fa` | `ar` | `en`. Multi-select as a list. */
      language?: string | string[];
      target_audience_id?: number | number[];
      educator_id?: number | number[];
      /** Ignored by a mini-app-scoped listing, which is already one shop. */
      mini_app_uuid?: string | string[];
      /** e.g. 4 for "۴ ستاره و بالاتر". */
      rating_min?: number;
      lessons_min?: number;
      lessons_max?: number;
    };
    sort?: string;
    page?: number;
    per_page?: number;
  }) {
    const searchParams = new URLSearchParams();
    const f = params?.filter;

    this.appendFilterParam(searchParams, "global", f?.global);
    this.appendFilterParam(searchParams, "title", f?.title);
    this.appendFilterParam(searchParams, "category_id", f?.category_id);
    this.appendFilterParam(searchParams, "category_title", f?.category_title);
    this.appendFilterParam(searchParams, "price_min", f?.price_min);
    this.appendFilterParam(searchParams, "price_max", f?.price_max);
    this.appendFilterParam(searchParams, "discount_min", f?.discount_min);
    this.appendFilterParam(searchParams, "discount_max", f?.discount_max);
    this.appendFilterParam(searchParams, "created_from", f?.created_from);
    this.appendFilterParam(searchParams, "created_to", f?.created_to);
    this.appendFilterParam(searchParams, "users_min", f?.users_min);
    this.appendFilterParam(searchParams, "users_max", f?.users_max);
    this.appendFilterParam(searchParams, "is_free", f?.is_free);
    this.appendFilterParam(searchParams, "has_certificate", f?.has_certificate);
    this.appendFilterParam(searchParams, "has_quiz", f?.has_quiz);
    this.appendFilterParam(
      searchParams,
      "has_online_class",
      f?.has_online_class,
    );
    this.appendFilterParam(searchParams, "level", f?.level);
    this.appendFilterParam(searchParams, "language", f?.language);
    this.appendFilterParam(
      searchParams,
      "target_audience_id",
      f?.target_audience_id,
    );
    this.appendFilterParam(searchParams, "educator_id", f?.educator_id);
    this.appendFilterParam(searchParams, "mini_app_uuid", f?.mini_app_uuid);
    this.appendFilterParam(searchParams, "rating_min", f?.rating_min);
    this.appendFilterParam(searchParams, "lessons_min", f?.lessons_min);
    this.appendFilterParam(searchParams, "lessons_max", f?.lessons_max);

    if (params?.sort) {
      searchParams.set("sort", params.sort);
    }
    if (typeof params?.page === "number") {
      searchParams.set("page", String(params.page));
    }
    if (typeof params?.per_page === "number") {
      searchParams.set("per_page", String(params.per_page));
    }

    const queryString = searchParams.toString();
    const endpoint = `vitrin/mini-apps/courses${queryString ? `?${queryString}` : ""}`;

    return fetchInstance<VitrinCourse[]>(endpoint, { method: "GET" });
  }

  /**
   * One educator's catalogue. Same param contract and same item shape as the
   * mini-app course list, so the vitrin course card renders it unchanged.
   *
   * `scope` decides which catalogue is being browsed: `vitrin` (the default)
   * is the marketplace, `miniapp` a storefront's own shelf, which also carries
   * courses never flagged for the vitrin. Asking for the wrong one doesn't
   * error — it just returns a suspiciously short list.
   *
   * An empty `data` is a valid answer (the educator publishes nothing in this
   * scope); only a 404 means the id itself is unknown.
   */
  static async getEducatorCourses(
    educatorId: string | number,
    params?: {
      scope?: "vitrin" | "miniapp";
      filter?: {
        global?: string;
        title?: string;
        category_id?: number | number[];
        category_title?: string;
        price_min?: number;
        price_max?: number;
        discount_min?: number;
        discount_max?: number;
        created_from?: string;
        created_to?: string;
        users_min?: number;
        users_max?: number;
      };
      sort?: string;
      page?: number;
      per_page?: number;
    },
  ) {
    const searchParams = new URLSearchParams();
    const f = params?.filter;

    if (params?.scope) {
      searchParams.set("scope", params.scope);
    }

    // appendFilterParam already drops undefined and NaN, so every optional
    // filter goes through the same line rather than its own guard.
    this.appendFilterParam(searchParams, "global", f?.global);
    this.appendFilterParam(searchParams, "title", f?.title);
    this.appendFilterParam(searchParams, "category_id", f?.category_id);
    this.appendFilterParam(searchParams, "category_title", f?.category_title);
    this.appendFilterParam(searchParams, "price_min", f?.price_min);
    this.appendFilterParam(searchParams, "price_max", f?.price_max);
    this.appendFilterParam(searchParams, "discount_min", f?.discount_min);
    this.appendFilterParam(searchParams, "discount_max", f?.discount_max);
    this.appendFilterParam(searchParams, "created_from", f?.created_from);
    this.appendFilterParam(searchParams, "created_to", f?.created_to);
    this.appendFilterParam(searchParams, "users_min", f?.users_min);
    this.appendFilterParam(searchParams, "users_max", f?.users_max);

    if (params?.sort) {
      searchParams.set("sort", params.sort);
    }
    if (typeof params?.page === "number") {
      searchParams.set("page", String(params.page));
    }
    if (typeof params?.per_page === "number") {
      searchParams.set("per_page", String(params.per_page));
    }

    const queryString = searchParams.toString();
    const endpoint = `vitrin/educators/${educatorId}/courses${
      queryString ? `?${queryString}` : ""
    }`;

    return fetchInstance<VitrinListedCourse[]>(endpoint, { method: "GET" });
  }

  static async getCategories() {
    const endpoint = `categories`;
    return fetchInstance<VitrinCategory[]>(endpoint, { method: "GET" });
  }

  /**
   * Autocomplete for the search box. Matches mid-word, not just prefixes, so
   * "عکا" finds «آموزش عکاسی».
   *
   * Two characters minimum: the API answers a shorter `q` with empty arrays
   * without touching the search cluster, so there is nothing to guard for
   * beyond a debounce. It answers its own errors with empty arrays too, never
   * an error status — a broken suggestion box should be invisible.
   *
   * A **404** is the one status worth reading: it means this API has no search
   * backend deployed, which callers use to fall back to {@link search}.
   */
  static async suggest(q: string) {
    const searchParams = new URLSearchParams({ q });
    const endpoint = `search/suggest?${searchParams.toString()}`;
    return fetchInstance<SearchSuggestions>(endpoint, { method: "GET" });
  }

  /**
   * Most-searched terms, for the chip strip under the search box. `limit`
   * defaults to 10 server-side and is capped at 20.
   *
   * An empty array is a legitimate answer (no telemetry yet, analytics off, or
   * nothing past the backend's thresholds) and the endpoint never answers with
   * an error status — a trending widget must not be able to break the page.
   *
   * As with {@link suggest}, a **404** means this API has no search backend
   * deployed, which is a different thing from "nothing is trending".
   */
  static async getPopularSearches(limit?: number) {
    const endpoint = `search/popular${this.buildLimitQuery(limit)}`;
    return fetchInstance<PopularSearchTerm[]>(endpoint, { method: "GET" });
  }

  static async search(
    query: string,
    params?: {
      page?: number;
      per_page?: number;
    },
  ) {
    const searchParams = new URLSearchParams();
    searchParams.set("query", query);

    if (typeof params?.page === "number") {
      searchParams.set("page", String(params.page));
    }

    if (typeof params?.per_page === "number") {
      searchParams.set("per_page", String(params.per_page));
    }

    const queryString = searchParams.toString();
    const endpoint = `search${queryString ? `?${queryString}` : ""}`;

    return fetchInstance<SearchResultData>(endpoint, { method: "GET" });
  }

  static async getCourseDetail(
    miniAppUuid: string | number,
    courseId: string | number,
  ) {
    const endpoint = `mini-apps/${miniAppUuid}/courses/${courseId}`;
    return fetchInstance<VitrinCourseDetail>(endpoint, { method: "GET" });
  }

  static async getCourseLessons(
    miniAppUuid: string | number,
    courseId: string | number,
    params?: {
      page?: number;
      perPage?: number;
    },
  ) {
    const searchParams = new URLSearchParams({
      per_page: String(params?.perPage ?? 500),
      page: String(params?.page ?? 1),
    });
    const endpoint = `mini-apps/${miniAppUuid}/courses/${courseId}/lessons?${searchParams.toString()}`;

    return fetchInstance<
      VitrinCourseDetailLessonItem[] | { data?: VitrinCourseDetailLessonItem[] }
    >(endpoint, { method: "GET", auth: true });
  }

  /**
   * SnappPay installment eligibility for a course. Amount is computed on the
   * backend from the course price (and the passed discount) — no amount is sent.
   * No auth required; it's a display hint. Re-call whenever the amount changes.
   */
  static async getSnappPayEligibility(
    miniAppUuid: string | number,
    courseId: string | number,
    discountId?: number,
  ) {
    const query =
      typeof discountId === "number" ? `?discount_id=${discountId}` : "";
    const endpoint = `mini-apps/${miniAppUuid}/courses/${courseId}/snapppay-eligibility${query}`;
    return fetchInstance<SnappPayEligibility>(endpoint, { method: "GET" });
  }

  static async buyCourse(
    miniAppUuid: string | number,
    courseId: string | number,
    body?: {
      discount_id?: number;
      source?: number;
      payment_method?: PaymentMethod;
    },
  ) {
    const endpoint = `mini-apps/${miniAppUuid}/courses/${courseId}/buy`;

    const utm = getBuyAttribution();

    return fetchInstance<BuyCourseResponse>(endpoint, {
      method: "POST",
      auth: true,
      body: {
        source: 1,
        ...body,
        ...utm,
      },
    });
  }

  static async getLessonContents(
    miniAppUuid: string | number,
    courseId: string | number,
    lessonId: string | number,
    params?: {
      page?: number;
      perPage?: number;
    },
  ) {
    const searchParams = new URLSearchParams({
      per_page: String(params?.perPage ?? 20),
      page: String(params?.page ?? 1),
    });
    const endpoint = `mini-apps/${miniAppUuid}/courses/${courseId}/lessons/${lessonId}/contents?${searchParams.toString()}`;

    return fetchInstance<CourseContent[]>(endpoint, {
      method: "GET",
      auth: true,
    });
  }
}
